import React from 'react';
import { Card, List, Space, Statistic, Switch, Typography, message, theme } from 'antd';
import { useAIBuilderApi } from '../../../api/aibuilder/aibuilder';
import { TDataset } from '../../../models/aibuilder/aibuilder';
import { getImagePath } from '../../../utils/format/valueFormat';
import { ImageModel } from '../engine/useImageModel';
import { TextModel } from '../engine/useTextModel';
import TrainStation from './TrainStation';

export interface ImproveStationProps {
  dataset: TDataset;
  imageModel: ImageModel;
  textModel: TextModel;
  purpose: string;
  onChanged: () => void;
  editable: boolean;
}

const ImproveStation: React.FC<ImproveStationProps> = ({ dataset, imageModel, textModel, purpose, onChanged, editable }) => {
  const { setSampleExcluded } = useAIBuilderApi();
  const { token } = theme.useToken();
  const isText = dataset.data_type === 'text';
  const versions = [...(dataset.models || [])].sort((a, b) => a.version - b.version);
  const latest = versions[versions.length - 1];
  const previous = versions[versions.length - 2];
  const delta = latest?.accuracy != null && previous?.accuracy != null
    ? latest.accuracy - previous.accuracy
    : null;

  const toggleSample = async (sampleId: number, checked: boolean) => {
    try {
      await setSampleExcluded(sampleId, !checked);
      onChanged();
    } catch (error: any) {
      message.error(error?.response?.data || error?.message || 'Modification impossible.');
    }
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <section aria-label="Inclure dans l'entraînement">
        <Typography.Title level={5}>Inclure dans l'entraînement</Typography.Title>
        <List
          dataSource={dataset.labels || []}
          locale={{ emptyText: 'Ajoutez un premier label.' }}
          renderItem={(label) => (
            <List.Item>
              <Card size="small" title={label.name}>
                <Space wrap>
                  {(dataset.samples || [])
                    .filter((sample) => sample.label_id === label.id)
                    .map((sample) =>
                      isText ? (
                        <Space key={sample.id} direction="vertical" align="start" style={{ maxWidth: 200 }}>
                          <Typography.Text>{sample.content_text}</Typography.Text>
                          <Switch
                            aria-label={`Inclure la phrase ${sample.id} de ${label.name} dans l'entraînement`}
                            checked={!sample.excluded}
                            onChange={(checked) => toggleSample(sample.id, checked)}
                            disabled={!editable}
                          />
                        </Space>
                      ) : (
                        <Space key={sample.id} direction="vertical" align="center">
                          <img
                            src={getImagePath(sample.content_url || '')}
                            alt={label.name}
                            width={72}
                            height={72}
                            style={{ objectFit: 'cover', borderRadius: 4 }}
                          />
                          <Switch
                            aria-label={`Inclure l'image ${sample.id} de ${label.name} dans l'entraînement`}
                            checked={!sample.excluded}
                            onChange={(checked) => toggleSample(sample.id, checked)}
                            disabled={!editable}
                          />
                        </Space>
                      ),
                    )}
                </Space>
              </Card>
            </List.Item>
          )}
        />
      </section>
      <TrainStation
        dataset={dataset}
        imageModel={imageModel}
        textModel={textModel}
        purpose={purpose}
        editable={editable}
        onChanged={onChanged}
      />
      <section aria-label="Historique des versions">
        <Typography.Title level={5}>Historique des versions</Typography.Title>
        <List
          dataSource={versions}
          locale={{ emptyText: 'Pas encore entraîné' }}
          renderItem={(version) => (
            <List.Item>
              v{version.version} — {version.accuracy != null ? `${version.accuracy}%` : '—'}
              {' — '}{version.purpose || '—'}{' — '}{version.created_at || '—'}
            </List.Item>
          )}
        />
        {delta !== null && (
          <Statistic
            title="Évolution de la précision"
            value={Math.abs(delta)}
            prefix={delta >= 0 ? '+' : '-'}
            suffix="pts"
            valueStyle={{ color: delta >= 0 ? token.colorSuccess : token.colorError }}
          />
        )}
      </section>
    </Space>
  );
};

export default ImproveStation;
